import { useState } from "react";
import { Share2 } from "lucide-react";
import { toast } from "sonner";

const W = 1080;
const H = 1920;

const loadAvatar = (src) =>
  new Promise((res) => {
    if (!src) return res(null);
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => res(img);
    img.onerror = () => res(null);
    img.src = src;
  });

const mono = (x, size, alpha = 1) => {
  x.font = `600 ${size}px 'IBM Plex Mono', monospace`;
  x.fillStyle = `rgba(255,255,255,${alpha})`;
};
const display = (x, size, alpha = 1) => {
  x.font = `400 ${size}px Anton, sans-serif`;
  x.fillStyle = `rgba(255,255,255,${alpha})`;
};

const draw = (x, user, savedCount, avatar) => {
  x.fillStyle = "#0A0A0A";
  x.fillRect(0, 0, W, H);
  x.strokeStyle = "rgba(255,255,255,0.25)";
  x.lineWidth = 3;
  x.strokeRect(48, 48, W - 96, H - 96);

  display(x, 66);
  x.fillText("NINJA RUN CLUB", 110, 210);
  mono(x, 30, 0.5);
  const year = user.created_at ? new Date(user.created_at).getFullYear() : new Date().getFullYear();
  x.fillText(`RUNNER CARD · MEMBER SINCE ${year}`, 110, 268);

  if (avatar) {
    x.save();
    x.beginPath();
    x.arc(210, 500, 100, 0, Math.PI * 2);
    x.closePath();
    x.clip();
    x.drawImage(avatar, 110, 400, 200, 200);
    x.restore();
    x.beginPath();
    x.arc(210, 500, 100, 0, Math.PI * 2);
    x.strokeStyle = "rgba(255,255,255,0.4)";
    x.stroke();
  } else {
    x.beginPath();
    x.arc(210, 500, 100, 0, Math.PI * 2);
    x.strokeStyle = "rgba(255,255,255,0.4)";
    x.stroke();
    display(x, 90);
    x.fillText((user.name || "?")[0].toUpperCase(), 180, 535);
  }

  const name = (user.name || "RUNNER").toUpperCase();
  const lines = name.length > 14 ? name.split(" ").reduce((acc, w) => {
    const last = acc[acc.length - 1];
    if (last && (last + " " + w).length <= 14) acc[acc.length - 1] = last + " " + w;
    else acc.push(w);
    return acc;
  }, []) : [name];
  display(x, 118);
  lines.slice(0, 2).forEach((l, i) => x.fillText(l, 110, 780 + i * 128));

  const statsY = 1050;
  x.strokeStyle = "rgba(255,255,255,0.15)";
  x.lineWidth = 2;
  x.beginPath();
  x.moveTo(110, statsY - 60);
  x.lineTo(W - 110, statsY - 60);
  x.stroke();

  const stats = [
    ["HOME CITY", user.city || "—"],
    ["EASY PACE", user.pace ? `${user.pace} /MI` : "—"],
    ["CHASING", user.goal || "—"],
    ["SAVED CLUBS", String(savedCount)],
  ];
  stats.forEach(([label, value], i) => {
    const col = i % 2;
    const row = Math.floor(i / 2);
    const sx = 110 + col * 440;
    const sy = statsY + row * 220;
    mono(x, 28, 0.4);
    x.fillText(label, sx, sy);
    display(x, 64);
    x.fillText(value.length > 12 ? value.slice(0, 12) + "…" : value, sx, sy + 78);
  });

  display(x, 60, 0.9);
  x.fillText("EVERY PACE HAS A PLACE.", 110, 1680);
  mono(x, 30, 0.5);
  x.fillText("NINJARUNCLUB.COM", 110, 1760);
};

export const RunnerCardShare = ({ user, savedCount }) => {
  const [busy, setBusy] = useState(false);

  const share = async () => {
    setBusy(true);
    try {
      await Promise.all([
        document.fonts.load("400 118px Anton"),
        document.fonts.load("600 30px 'IBM Plex Mono'"),
      ]).catch(() => {});
      const avatar = await loadAvatar(user.picture);
      const c = document.createElement("canvas");
      c.width = W;
      c.height = H;
      draw(c.getContext("2d"), user, savedCount, avatar);
      const blob = await new Promise((r) => c.toBlob(r, "image/png"));
      const file = new File([blob], "nrc-runner-card.png", { type: "image/png" });
      if (navigator.canShare?.({ files: [file] })) {
        await navigator.share({ files: [file], title: "My NRC Runner Card" });
      } else {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "nrc-runner-card.png";
        a.click();
        URL.revokeObjectURL(url);
        toast.success("Runner card downloaded — post it to your story.");
      }
    } catch (e) {
      if (e?.name !== "AbortError") toast.error("Could not build your card. Try again.");
    }
    setBusy(false);
  };

  return (
    <button
      data-testid="profile-share-card"
      onClick={share}
      disabled={busy}
      className="btn-invert inline-flex items-center gap-2.5 bg-white text-ink font-mono-label px-6 py-3.5 border border-white hover:bg-transparent hover:text-white disabled:opacity-50"
    >
      <Share2 size={14} /> {busy ? "BUILDING..." : "SHARE MY CARD"}
    </button>
  );
};
